import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, Check, X } from "lucide-react";

// Polled return page after Stripe Checkout
export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", message: "Confirming your payment…" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let sessionId = params.get("session_id");
    if (!sessionId) {
      try {
        sessionId = localStorage.getItem("tiplingo_last_checkout");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("localStorage read failed:", err?.message || err);
        }
      }
    }
    if (!sessionId) {
      setState({ status: "error", message: "No session id provided." });
      return;
    }

    let cancelled = false;
    async function poll() {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        const ps = r.data.payment_status;
        if (ps === "paid") {
          await refresh();
          try { localStorage.removeItem("tiplingo_last_checkout"); } catch (e) { void e; }
          setState({ status: "ok", message: "Payment confirmed! Your access is updated." });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "error", message: "Payment session expired." });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "error", message: "Still processing — check your email or refresh in a moment." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attemptsRef.current >= 8) {
          setState({ status: "error", message: "Couldn't verify payment. Try again later." });
          return;
        }
        setTimeout(poll, 2000);
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [location.search, refresh]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bone/95 px-4">
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-deep p-7 text-center space-y-5"
        data-testid="payment-return-card"
      >
        {/* Brand wordmark anchors the screen so it feels like part of
            TipLingo even before the back button is tapped. */}
        <div className="font-jakarta font-extrabold text-3xl text-navy leading-none">
          Tip<span className="text-orange">Lingo</span>
        </div>

        {/* Status indicator */}
        <div className="flex justify-center">
          {state.status === "checking" && (
            <div
              className="w-14 h-14 rounded-full bg-bone/60 flex items-center justify-center"
              aria-label="Confirming"
            >
              <Loader2 className="animate-spin text-orange" size={28} strokeWidth={2.5} />
            </div>
          )}
          {state.status === "ok" && (
            <div
              className="w-14 h-14 rounded-full bg-forest flex items-center justify-center"
              aria-label="Success"
            >
              <Check className="text-white" size={30} strokeWidth={3} />
            </div>
          )}
          {state.status === "error" && (
            <div
              className="w-14 h-14 rounded-full bg-orange flex items-center justify-center"
              aria-label="Error"
            >
              <X className="text-white" size={30} strokeWidth={3} />
            </div>
          )}
        </div>

        {/* Headline + sub-message */}
        <div>
          <h2 className="font-jakarta font-extrabold text-navy text-xl leading-tight">
            {state.status === "checking" && "Confirming your payment"}
            {state.status === "ok" && "Welcome to Pro!"}
            {state.status === "error" && "Couldn't confirm payment"}
          </h2>
          <p className="font-jakarta text-navy-soft text-sm mt-2 leading-relaxed">
            {state.message}
          </p>
        </div>

        {/* CTA — orange pill in TipLingo v2 style */}
        <button
          onClick={() => navigate("/", { replace: true })}
          data-testid="payment-return-back"
          className="w-full bg-orange hover:bg-orange-hover text-white rounded-full py-3.5 font-jakarta font-extrabold text-sm active:scale-[0.98] transition-transform shadow-soft"
        >
          Back to TipLingo
        </button>
      </div>
    </div>
  );
}
